// Wrapper de API do módulo Controle de Caixas (controle interno isolado). Passa por lib/api.ts.
// O backend é a fonte de verdade do eixo financeiro: para o CONFERENTE os campos de valor vêm nulos e as
// baixas de pagamento são omitidas — a UI apenas reflete o que o backend entrega. Material/tipo vêm de um
// CATÁLOGO DINÂMICO cadastrável pelo ADMIN; os lançamentos carregam o NOME congelado (materialName/typeName).
import { requestJson, downloadFile } from "@/lib/api";

export type BoxOperation = "EMPRESTIMO" | "VENDA";
export type BoxPaymentMethod = "DINHEIRO" | "PIX" | "CARTAO" | "TRANSFERENCIA" | "OUTRO";
export type BoxSettlementType = "DEVOLUCAO" | "PAGAMENTO";
export type BoxMovementStatus =
  | "PENDENTE"
  | "PARCIALMENTE_DEVOLVIDO"
  | "DEVOLVIDO"
  | "PARCIALMENTE_PAGO"
  | "PAGO"
  | "ESTORNADO";

// ----- Catálogo (materiais e tipos) -----
export interface BoxCatalogType {
  id: string;
  name: string;
  active: boolean;
}

export interface BoxCatalogMaterial {
  id: string;
  name: string;
  active: boolean;
  hasTypes: boolean;
  types: BoxCatalogType[];
}

export interface BoxPrice {
  id: string;
  materialId: string;
  typeId: string | null;
  materialName: string;
  typeName: string | null;
  materialActive: boolean;
  typeActive: boolean;
  referenceQuantity: number | null;
  referencePrice: number | null;
  priced: boolean;
}

export interface BoxPriceChange {
  materialName: string;
  typeName: string | null;
  oldReferenceQuantity: number | null;
  oldReferencePrice: number | null;
  newReferenceQuantity: number | null;
  newReferencePrice: number | null;
  changedAt: string;
  actorEmail: string;
  actorRole: string;
}

export interface BoxPerson {
  id: string;
  name: string;
  phone: string | null;
  notes: string | null;
}

export interface BoxSettlement {
  id: string;
  itemId: string | null;
  type: BoxSettlementType;
  quantity: number | null;
  amount: number | null;
  paymentMethod: BoxPaymentMethod | null;
  settledDate: string;
  observation: string | null;
  createdByEmail: string;
  createdAt: string;
  reversed: boolean;
  reversalReason: string | null;
}

export interface BoxMovementItem {
  itemId: string;
  materialId: string;
  typeId: string | null;
  materialName: string;
  typeName: string | null;
  quantity: number;
  returnedQuantity: number | null;
  pendingQuantity: number | null;
  saleReferenceQuantity: number | null;
  saleReferencePrice: number | null;
  saleLineTotal: number | null;
}

export interface BoxMovement {
  id: string;
  operation: BoxOperation;
  operationDate: string;
  withdrawnAt: string;
  personId: string;
  personName: string;
  observation: string | null;
  status: BoxMovementStatus;
  items: BoxMovementItem[];
  financialVisible: boolean;
  saleTotal: number | null;
  receivedAmount: number | null;
  pendingAmount: number | null;
  createdByEmail: string;
  createdAt: string;
  reversed: boolean;
  reversalReason: string | null;
  reversedByEmail: string | null;
  reversedAt: string | null;
  signatureImage: string | null;
  signedSummary: string | null;
  settlements: BoxSettlement[] | null;
}

export interface BoxReport {
  from: string | null;
  to: string | null;
  financialVisible: boolean;
  movements: BoxMovement[];
  totalsByMaterialType: {
    materialId: string;
    typeId: string | null;
    materialName: string;
    typeName: string | null;
    loanedQuantity: number;
    returnedQuantity: number;
    pendingLoanQuantity: number;
    soldQuantity: number;
  }[];
  totalsByOperation: {
    loanCount: number;
    loanedQuantity: number;
    returnedQuantity: number;
    pendingLoanQuantity: number;
    saleCount: number;
    soldQuantity: number;
    salesTotal: number | null;
    receivedTotal: number | null;
    pendingSalesTotal: number | null;
  };
  generatedAt: string;
}

export interface BoxMovementFilters {
  from?: string;
  to?: string;
  personId?: string;
  materialId?: string;
  typeId?: string;
  operation?: BoxOperation;
  status?: BoxMovementStatus;
}

const BASE = "/api/v1/admin/box";

function queryString(filters: BoxMovementFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

// ----- Catálogo (leitura: operacional; gestão: ADMIN) -----
export function fetchBoxCatalog(): Promise<BoxCatalogMaterial[]> {
  return requestJson<BoxCatalogMaterial[]>(`${BASE}/catalog/materials`);
}

export function createBoxMaterial(body: { name: string; active?: boolean }): Promise<BoxCatalogMaterial> {
  return requestJson<BoxCatalogMaterial>(`${BASE}/catalog/materials`, { method: "POST", body });
}

export function updateBoxMaterial(
  id: string, body: { name: string; active: boolean },
): Promise<BoxCatalogMaterial> {
  return requestJson<BoxCatalogMaterial>(`${BASE}/catalog/materials/${id}`, { method: "PUT", body });
}

export function createBoxType(
  materialId: string, body: { name: string; active?: boolean },
): Promise<BoxCatalogType> {
  return requestJson<BoxCatalogType>(`${BASE}/catalog/materials/${materialId}/types`, { method: "POST", body });
}

export function updateBoxType(
  typeId: string, body: { name: string; active: boolean },
): Promise<BoxCatalogType> {
  return requestJson<BoxCatalogType>(`${BASE}/catalog/types/${typeId}`, { method: "PUT", body });
}

// ----- Preços (ADMIN) -----
export function fetchBoxPrices(): Promise<BoxPrice[]> {
  return requestJson<BoxPrice[]>(`${BASE}/prices`);
}

export function fetchBoxPriceHistory(): Promise<BoxPriceChange[]> {
  return requestJson<BoxPriceChange[]>(`${BASE}/prices/history`);
}

export function updateBoxPrice(
  priceId: string,
  body: { referenceQuantity: number; referencePrice: number },
): Promise<BoxPrice> {
  return requestJson<BoxPrice>(`${BASE}/prices/${priceId}`, { method: "PUT", body });
}

// ----- Pessoas -----
export function fetchBoxPeople(): Promise<BoxPerson[]> {
  return requestJson<BoxPerson[]>(`${BASE}/people`);
}

export function createBoxPerson(body: { name: string; phone?: string; notes?: string }): Promise<BoxPerson> {
  return requestJson<BoxPerson>(`${BASE}/people`, { method: "POST", body });
}

// ----- Lançamentos e baixas -----
export function fetchBoxMovements(filters: BoxMovementFilters = {}): Promise<BoxMovement[]> {
  return requestJson<BoxMovement[]>(`${BASE}/movements${queryString(filters)}`);
}

export function fetchBoxMovement(id: string): Promise<BoxMovement> {
  return requestJson<BoxMovement>(`${BASE}/movements/${id}`);
}

export interface CreateBoxMovementItemInput {
  materialId: string;
  typeId: string | null;
  quantity: number;
}

export interface CreateBoxMovementInput {
  operation: BoxOperation;
  personId?: string;
  newPersonName?: string;
  newPersonPhone?: string;
  newPersonNotes?: string;
  items: CreateBoxMovementItemInput[];
  operationDate: string;
  observation?: string;
  signatureImage: string;
  signedSummary: string;
  clientRequestId: string;
}

export function createBoxMovement(body: CreateBoxMovementInput): Promise<BoxMovement> {
  return requestJson<BoxMovement>(`${BASE}/movements`, { method: "POST", body });
}

export function registerBoxReturn(
  id: string,
  body: { itemId: string; quantity: number; settledDate: string; observation?: string; clientRequestId: string },
): Promise<BoxMovement> {
  return requestJson<BoxMovement>(`${BASE}/movements/${id}/returns`, { method: "POST", body });
}

export function registerBoxPayment(
  id: string,
  body: {
    amount: number;
    paymentMethod: BoxPaymentMethod;
    settledDate: string;
    observation?: string;
    clientRequestId: string;
  },
): Promise<BoxMovement> {
  return requestJson<BoxMovement>(`${BASE}/movements/${id}/payments`, { method: "POST", body });
}

export function reverseBoxMovement(id: string, reason: string): Promise<BoxMovement> {
  return requestJson<BoxMovement>(`${BASE}/movements/${id}/reversal`, { method: "POST", body: { reason } });
}

export function reverseBoxSettlement(settlementId: string, reason: string): Promise<BoxMovement> {
  return requestJson<BoxMovement>(`${BASE}/movements/settlements/${settlementId}/reversal`, {
    method: "POST",
    body: { reason },
  });
}

export function downloadBoxReceipt(id: string): Promise<void> {
  return downloadFile(`${BASE}/movements/${id}/receipt.pdf`, `comprovante-caixas_${id}.pdf`);
}

// ----- Relatório -----
export function fetchBoxReport(filters: BoxMovementFilters = {}): Promise<BoxReport> {
  return requestJson<BoxReport>(`${BASE}/report${queryString(filters)}`);
}

export function downloadBoxReport(format: "pdf" | "xlsx", filters: BoxMovementFilters = {}): Promise<void> {
  return downloadFile(`${BASE}/report/export.${format}${queryString(filters)}`, `controle-caixas.${format}`);
}
